export function GetScanResultMerged(scanDetails, weight, moisture) {
  const firstDetail = scanDetails[0];
  let total_kernel_count = 0;
  let total_kernel_weight = 0;
  const final_counts = {};
  const final_areas = {};
  const area_counts = {};
  const final_lengths = {};
  const length_counts = {};
  const final_weights = {};
  let final_length_dists = null;
  let final_grading = firstDetail.kernel_details?.grading;
  let Final_class_ranges = {};
  const final_kernel_details = {
    ...firstDetail.kernel_details,
    beautify: firstDetail.kernel_details.beautify,
    classes: firstDetail.kernel_details.classes,
    consider_area: firstDetail.kernel_details.consider_area,
    consider_length: firstDetail.kernel_details.consider_length,
    consider_length_dist: firstDetail.kernel_details.consider_length_dist,
    quality_score_dependencies: firstDetail.kernel_details.quality_score_dependencies
  };

  for (const detail of scanDetails) {
    total_kernel_count += detail.kernel_details.total_kernel_count;
    total_kernel_weight += detail.kernel_details.total_kernel_weight;

    for (const [kernel_class, count] of Object.entries(detail.kernel_details.kernel_counts || {})) {
      final_counts[kernel_class] = (final_counts[kernel_class] || 0) + count;

      if (detail.kernel_details.consider_area) {
        const area = detail.kernel_details.areas?.[kernel_class];
        if (area !== undefined) {
          final_areas[kernel_class] = (final_areas[kernel_class] || 0) + area;
          area_counts[kernel_class] = (area_counts[kernel_class] || 0) + 1;
        }
      }

      if (detail.kernel_details.consider_length) {
        const length = detail.kernel_details.kernel_lengths?.[kernel_class];
        if (length !== undefined) {
          final_lengths[kernel_class] = (final_lengths[kernel_class] || 0) + length;
          length_counts[kernel_class] = (length_counts[kernel_class] || 0) + 1;
        }
      }

      if (detail.kernel_details.total_kernel_weight > 0) {
        const weight =
          detail.kernel_details.kernel_weights?.[kernel_class] ??
          detail.kernel_details.kernel_weight?.[kernel_class];
        if (weight !== undefined) {
          final_weights[kernel_class] = (final_weights[kernel_class] || 0) + weight;
        }
      }
    }

    if (detail.kernel_details.range_distributions) {
      const rangeDist = detail.kernel_details.range_distributions;
      const translations =
        detail.kernel_details.beautify?.translations?.range_distributions ??
        detail.kernel_details.beautify?.translations?.length_distributions;

      const distributions = rangeDist.distributions;
      const labels = rangeDist.labels || {};

      if (!final_length_dists) {
        final_length_dists = {
          range: null,
          distributions: {},
          matrix: {
            rows: labels.widths || [],
            columns: labels.heights || [],
            data: [],
          },
        };
      }

      if (Array.isArray(distributions) && Array.isArray(distributions[0]) && labels.heights && labels.widths) {
        const heights = labels.heights;
        const widths = labels.widths;

        // Initialize the matrix if it's the first time
        if (final_length_dists.matrix.data.length === 0) {
          final_length_dists.matrix.data = Array.from({ length: widths.length }, () =>
            Array(heights.length).fill(0)
          );
        }

        for (let i = 0; i < widths.length; i++) {
          const row = distributions[i] || [];

          for (let j = 0; j < heights.length; j++) {
            const value = row[j] || 0;

            // Add to the final matrix
            final_length_dists.matrix.data[i][j] += value;

            // Add to the flat label-based distribution too
            const label = `${widths[i]} | ${heights[j]}`;
            final_length_dists.distributions[label] = (final_length_dists.distributions[label] || 0) + value;
          }
        }
      } else {
        for (const [key, value] of Object.entries(distributions || {}).sort((a, b) => {
          const getStart = (str) => parseFloat(str.match(/(\d+\.?\d*)mm/)[1]);
          return getStart(a[0]) - getStart(b[0]);
        })) {
          const label = translations?.[key] || key;
          final_length_dists.distributions[label] =
            (final_length_dists.distributions[label] || 0) + Number(value);
        }
      }
    }
  }

  // Normalize averages
  for (const key in final_areas) {
    if (area_counts[key]) {
      final_areas[key] /= area_counts[key];
    }
  }

  for (const key in final_lengths) {
    if (length_counts[key]) {
      final_lengths[key] /= length_counts[key];
    }
  }

  if (firstDetail?.kernel_details?.grading) {
    const transformedGrading = {};

    // Check if all ranges have the first two values as zero
    const isRangeZeroForAll = Object.values(firstDetail?.kernel_details?.grading).every((property) => {
      const range = property?.weightRange || property?.countRange;
      return range && range[0] === 0 && range[1] === 0;
    });

    if (isRangeZeroForAll) {
      Object.keys(firstDetail?.kernel_details?.grading).forEach((key) => {
        const gradingProperty = firstDetail?.kernel_details?.grading[key];
        const range = gradingProperty?.weightRange || gradingProperty?.countRange;

        if (range) {
          transformedGrading[key] = gradingProperty?.weightRange
            ? { weightRange: [range[2] || 0] }
            : { countRange: [range[2] || 0] };
        }
      });

      final_kernel_details.grading = transformedGrading;
      final_kernel_details.max_limit = true;
    } else {
      const processGrading = () => {
        const classNames = firstDetail?.kernel_details?.classes;
        classNames?.forEach((className) => {
          const countPercentage = Number(
            (final_counts[className] / total_kernel_count) * 100
          ).toFixed(2);
          const weightPercentage = Number(
            (final_weights[className] / total_kernel_weight) * 100
          ).toFixed(2);
          const moisturePercentage = firstDetail?.scan_metadata?.moisture;
          const moistureRange =
            firstDetail?.kernel_details?.grading?.moisture?.countRange ||
            firstDetail?.kernel_details?.grading?.moisture?.weightRange ||
            [];
          const grade = calculateGradeAndRange(
            firstDetail?.kernel_details?.grading,
            className,
            countPercentage,
            weightPercentage
          );
          Final_class_ranges[className] = grade;
          const percentageMoistureRange = calculateMoistureRange(
            moisturePercentage,
            moistureRange
          );
          final_kernel_details.moisture_range = percentageMoistureRange;
        });
      };
      processGrading();
      final_kernel_details.class_ranges = Final_class_ranges;
      final_kernel_details.grading = final_grading;
    }

  }

  const totalCountStr = `Total Kernels (${total_kernel_count.toFixed(0)})`;
  if (final_kernel_details?.beautify?.translations) {
    final_kernel_details.beautify.translations.total_kernel_count = totalCountStr;
  }

  let acc = 0;
  const dependencies = final_kernel_details.quality_score_dependencies || [];

  for (const key of dependencies) {
    const count = final_counts[key];
    if (count !== undefined && total_kernel_count > 0) {
      const percentage = (count / total_kernel_count) * 100;
      if (!isNaN(percentage)) acc += percentage;
    }
  }

  final_kernel_details.overall_score = parseFloat((acc).toFixed(2));
  final_kernel_details.total_kernel_count = total_kernel_count;
  final_kernel_details.total_kernel_weight = total_kernel_weight;
  final_kernel_details.kernel_counts = final_counts;
  final_kernel_details.kernel_weights = final_weights;
  final_kernel_details.areas = final_areas;
  final_kernel_details.kernel_lengths = final_lengths;
  final_kernel_details.range_distributions = final_length_dists;
  return {
    dist_avg_indexes: firstDetail.dist_avg_indexes,
    kernel_details: final_kernel_details,
    processed_image: firstDetail.processed_image,
    weight_value: weight,
    moisture_value: moisture
  };
}

export function calculateGradeAndRange(classRanges, className, countPercentage, weightPercentage) {
  const range = classRanges[className];
  if (range) {
    let countRangeValue = 0;
    let weightRangeValue = 0;
    if (range.countRange && range.countRange.length > 0) {
      const [range1, range2] = range.countRange;
      if (countPercentage <= range1) {
        countRangeValue = 1
      } else if (range1 < countPercentage && countPercentage <= range2) {
        countRangeValue = 2
      } else if (range2 < countPercentage) {
        countRangeValue = 3
      }
    }
    if (range.weightRange && range.weightRange.length > 0) {
      const [range1, range2] = range.weightRange;
      if (weightPercentage <= range1) {
        weightRangeValue = 1
      } else if (range1 < weightPercentage && weightPercentage <= range2) {
        weightRangeValue = 2
      } else if (range2 < weightPercentage) {
        weightRangeValue = 3
      }
    }
    return weightRangeValue || countRangeValue;
  }
  return 'NA';
}

export function calculateMoistureRange(moisturePercentage, moistureRange) {
  let moistureRangeValue = 0
  if (moisturePercentage) {
    const [range1, range2] = moistureRange;
    if (moisturePercentage <= range1) {
      moistureRangeValue = 1
    } else if (range1 < moisturePercentage && moisturePercentage <= range2) {
      moistureRangeValue = 2
    } else if (range2 < moisturePercentage) {
      moistureRangeValue = 3
    }
    return moistureRangeValue
  }
  return 'NA';
}
